import type { CSSProperties, ReactNode } from "react";

const COS = 0.866;

/** A point of the isometric world (x to the right-down, y to the left-down, z up) on the 320 x 320 drawing. */
export const P = (x: number, y: number, z: number): [number, number] => [(x - y) * COS, (x + y) * 0.5 - z];
const pts = (list: [number, number, number][]) => list.map(([x, y, z]) => P(x, y, z).map((n) => n.toFixed(1)).join(",")).join(" ");

const mix = (pct: number, over = "var(--brand)") => `color-mix(in oklab, var(--brand-foreground) ${pct}%, ${over})`;
export const GOLD = "#f2c94c";
export const SHADES = { top: mix(96), left: mix(74), right: mix(52), edge: mix(30) };

/** Small gold sparks around the scene (x, y, radius). */
const SPARKS: [number, number, number][] = [[30, 70, 2.5], [290, 60, 3], [300, 190, 2], [24, 210, 2.5], [250, 20, 2]];

const MOTION = `
.ha-blink{animation:ha-blink 1.8s ease-in-out infinite}
.ha-flow{animation:ha-flow 2.4s linear infinite}
.ha-draw{transform-box:fill-box;transform-origin:left center;animation:ha-draw 1.1s ease-out both}
.ha-rise{animation:ha-rise .9s cubic-bezier(.2,.8,.2,1) both}
.ha-float{animation:ha-float 5s ease-in-out infinite}
.ha-float2{animation:ha-float 6.5s ease-in-out -2s infinite}
.ha-ping{transform-box:fill-box;transform-origin:center;animation:ha-ping 2.6s ease-out infinite}
.ha-enter{animation:ha-enter .9s cubic-bezier(.2,.8,.2,1) both}
.ha-grow{transform-box:fill-box;transform-origin:center bottom;animation:ha-grow 3.2s ease-in-out infinite alternate}
.ha-scan{animation:ha-scan 3s ease-in-out infinite}
.ha-spin{transform-box:fill-box;transform-origin:center;animation:ha-spin 6s ease-in-out infinite alternate}
.ha-pulse{transform-box:fill-box;transform-origin:center;animation:ha-pulse 1.6s ease-in-out infinite}
.ha-type{transform-box:fill-box;transform-origin:left center;animation:ha-type 4s ease-in-out infinite}
.ha-rot{transform-box:fill-box;transform-origin:center;animation:ha-rot 14s linear infinite}
.ha-rotr{transform-box:fill-box;transform-origin:center;animation:ha-rot 9s linear infinite reverse}
.ha-packet{offset-rotate:0deg;animation:ha-packet 3s linear infinite}
@keyframes ha-enter{from{opacity:0;transform:scale(.92) translateY(12px)}to{opacity:1;transform:none}}
@keyframes ha-grow{from{transform:scaleY(.35)}to{transform:scaleY(1)}}
@keyframes ha-scan{0%{transform:translateX(-30px);opacity:0}15%,85%{opacity:.9}100%{transform:translateX(100px);opacity:0}}
@keyframes ha-spin{from{transform:scaleX(.15)}to{transform:scaleX(1)}}
@keyframes ha-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.18)}}
@keyframes ha-type{0%{transform:scaleX(0)}40%,80%{transform:scaleX(1)}100%{transform:scaleX(0)}}
@keyframes ha-rot{to{transform:rotate(360deg)}}
@keyframes ha-blink{0%,100%{opacity:1}50%{opacity:.2}}
@keyframes ha-flow{to{stroke-dashoffset:-24}}
@keyframes ha-draw{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes ha-rise{from{transform:translateY(22px);opacity:0}to{transform:none;opacity:1}}
@keyframes ha-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes ha-ping{from{transform:scale(.5);opacity:.9}to{transform:scale(1.6);opacity:0}}
@keyframes ha-packet{from{offset-distance:0%}to{offset-distance:100%}}
@media (prefers-reduced-motion:reduce){[class^="ha-"]{animation:none!important}}`;

/** The drawing's canvas: a soft glow behind, the scene drawn in the hero's own colours. Decorative, so hidden from screen readers. */
export function Frame({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 320 320" aria-hidden className="ha-enter size-full overflow-visible text-brand-foreground" fill="none" strokeLinejoin="round" strokeLinecap="round">
      <style>{MOTION}</style>
      <defs>
        <radialGradient id="ha-glow" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="var(--brand-foreground)" stopOpacity=".28" /><stop offset="1" stopColor="var(--brand-foreground)" stopOpacity="0" /></radialGradient>
        <radialGradient id="ha-shadow" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#000" stopOpacity=".45" /><stop offset="1" stopColor="#000" stopOpacity="0" /></radialGradient>
        <linearGradient id="ha-screen" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="color-mix(in oklab, var(--brand) 70%, black)" /><stop offset="1" stopColor="var(--brand)" /></linearGradient>
      </defs>
      <circle cx="160" cy="170" r="150" fill="url(#ha-glow)" />
      {SPARKS.map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} fill={GOLD} className="ha-blink" style={{ animationDelay: `${i * 0.7}s` }} />)}
      {children}
    </svg>
  );
}

/** A soft shadow on the floor under a scene. */
export function Shadow({ x, y, rx, ry }: { x: number; y: number; rx: number; ry: number }) {
  return <ellipse cx={x} cy={y} rx={rx} ry={ry} fill="url(#ha-shadow)" />;
}

/** A box: its top, its front-left face (y side) and its front-right face (x side), lit from above. */
export function Box({ x, y, z, w, d, h, shades = SHADES, className, style }: { x: number; y: number; z: number; w: number; d: number; h: number; shades?: typeof SHADES; className?: string; style?: CSSProperties }) {
  return (
    <g className={className} style={style} stroke={shades.edge} strokeWidth="1">
      <polygon points={pts([[x, y + d, z], [x + w, y + d, z], [x + w, y + d, z + h], [x, y + d, z + h]])} style={{ fill: shades.left }} />
      <polygon points={pts([[x + w, y, z], [x + w, y + d, z], [x + w, y + d, z + h], [x + w, y, z + h]])} style={{ fill: shades.right }} />
      <polygon points={pts([[x, y, z + h], [x + w, y, z + h], [x + w, y + d, z + h], [x, y + d, z + h]])} style={{ fill: shades.top }} />
    </g>
  );
}

/** Flat drawing laid on the front-left face of a box (u to the right, v down), so screens and labels follow the perspective. */
export function Face({ x, y, z, children }: { x: number; y: number; z: number; children: ReactNode }) {
  const [px, py] = P(x, y, z);
  return <g transform={`matrix(${COS} 0.5 0 1 ${px.toFixed(1)} ${py.toFixed(1)})`}>{children}</g>;
}

/** A can (disk, bucket, database): `cy` is the middle of its top ellipse, `h` its height. */
export function Cylinder({ cx, cy, rx, ry, h, className, style }: { cx: number; cy: number; rx: number; ry: number; h: number; className?: string; style?: CSSProperties }) {
  return (
    <g className={className} style={style} stroke={SHADES.edge} strokeWidth="1">
      <path d={`M${cx - rx} ${cy}v${h}a${rx} ${ry} 0 0 0 ${2 * rx} 0v${-h}z`} style={{ fill: SHADES.left }} />
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} style={{ fill: SHADES.top }} />
    </g>
  );
}
